// src/components/ui/alert-dialog.jsx
import React, { createContext, useContext } from "react";
import { X } from "lucide-react";

const AlertDialogContext = createContext(null);

export function AlertDialog({ children, open, onOpenChange }) {
  return (
    <AlertDialogContext.Provider value={{ open, onOpenChange }}>
      {children}
    </AlertDialogContext.Provider>
  );
}

export function AlertDialogContent({
  children,
  className = "",
  size = "default", // Can be "default", "large", "full", or custom width in className
  ...props
}) {
  const { open, onOpenChange } = useContext(AlertDialogContext);

  if (!open) return null;

  const sizeClasses = {
    default: "max-w-md",
    large: "max-w-4xl",
    full: "max-w-[calc(100vw-2rem)] md:max-w-[calc(100vw-6rem)]",
  };

  const widthClass = sizeClasses[size] || "";

  return (
    <>
      {/* Backdrop with blur */}
      <div
        className="fixed inset-0 z-50 bg-black/30 backdrop-blur-[2px] transition-all"
        onClick={() => onOpenChange(false)}
      />

      {/* Modal container centered with max height */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8">
        <div
          className={`${widthClass} w-full max-h-[calc(100vh-3rem)] overflow-auto bg-white rounded-lg shadow-xl transform transition-all duration-200 ${className}`}
          onClick={(e) => e.stopPropagation()}
          {...props}
        >
          {/* Close button at top-right */}
          <button
            className="absolute right-4 top-4 rounded-full p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-5 w-5" />
          </button>

          {children}
        </div>
      </div>
    </>
  );
}

export function AlertDialogHeader({ children, className = "", ...props }) {
  return (
    <div className={`px-6 pt-6 pb-4 ${className}`} {...props}>
      {children}
    </div>
  );
}

export function AlertDialogTitle({ children, className = "", ...props }) {
  return (
    <h2
      className={`text-xl font-semibold text-gray-900 ${className}`}
      {...props}
    >
      {children}
    </h2>
  );
}

export function AlertDialogDescription({ children, className = "", ...props }) {
  return (
    <p className={`mt-2 text-sm text-gray-500 ${className}`} {...props}>
      {children}
    </p>
  );
}

export function AlertDialogFooter({ children, className = "", ...props }) {
  return (
    <div
      className={`flex justify-end space-x-2 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-lg ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

export function AlertDialogAction({ children, className = "", ...props }) {
  const { onOpenChange } = useContext(AlertDialogContext);

  const handleClick = (e) => {
    if (props.onClick) {
      props.onClick(e);
    }
    onOpenChange(false);
  };

  return (
    <button
      className={`px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${className}`}
      onClick={handleClick}
      {...props}
    >
      {children}
    </button>
  );
}

export function AlertDialogCancel({ children, className = "", ...props }) {
  const { onOpenChange } = useContext(AlertDialogContext);

  return (
    <button
      className={`px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${className}`}
      onClick={() => onOpenChange(false)}
      {...props}
    >
      {children || "Cancel"}
    </button>
  );
}
